import React, { useEffect, useMemo, useState } from 'react';
import * as zod from 'zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Form,
  FormSection,
  Input,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
} from '../ui';
import type {
  MrForm_CargoMeasurementFragment,
  PortCallsDocuments__OrganizationFragment,
  PortCallsDocuments__PortCallFragment,
} from '../../graphql';
import {
  useCreateCargoMeasurementMutation,
  useUpdateCargoMeasurementMutation,
  VolumeUnits,
  WeightUnits,
} from '../../graphql';
import { stringToNumber } from '../../lib/zod-util';
import {
  massCapacityUnits,
  volumeCapacityUnits,
} from '../../constants/capacityUnits';
import intersectionWith from 'lodash/intersectionWith';
import type { Id } from '../../lib/util';
import { isDefined } from '../../lib/util';
import differenceWith from 'lodash/differenceWith';
import { getOrderDisplayName } from '../order/order';
import { gql } from '@apollo/client';
import type { ScenarioOrder } from '../../lib/planningData';

const massUnitOptions = Object.keys(massCapacityUnits).map(
  (key: WeightUnits) => ({
    name: massCapacityUnits[key].symbol,
    id: key,
  }),
);

const volumeUnitOptions = Object.keys(volumeCapacityUnits).map(
  (key: VolumeUnits) => ({
    name: volumeCapacityUnits[key].symbol,
    id: key,
  }),
);

export interface MRFormProps {
  portcalls: Map<number, PortCallsDocuments__PortCallFragment>;
  organizations: Map<number, PortCallsDocuments__OrganizationFragment>;
  measurements: MrForm_CargoMeasurementFragment[];
  onSave?: () => void;
  orders: ScenarioOrder[];
  portCallId: number;
  userId: number;
  locationId: number | undefined;
  readOnly?: boolean;
}

const measurementSchema = zod.object({
  id: zod.number().nullable(),
  orderId: zod.number(),
  locationId: zod.number().nullable(),
  measuredVolume: zod.number().nullable(),
  rejectedVolume: zod.number().nullable(),
  volumeUnit: zod.nativeEnum(VolumeUnits).nullable(),
  measuredWeight: zod.number().nullable(),
  rejectedWeight: zod.number().nullable(),
  weightUnit: zod.nativeEnum(WeightUnits).nullable(),
});

type MeasurementType = zod.infer<typeof measurementSchema>;

const MRSchema = zod.object({
  measurements: zod.array(measurementSchema),
});

type MRFormFields = zod.infer<typeof MRSchema>;

const MRForm = ({
  portcalls,
  organizations,
  measurements,
  locationId,
  onSave,
  orders,
  portCallId,
  userId,
  readOnly,
}: MRFormProps): JSX.Element => {
  const [
    createCargoMeasurement,
    {
      data: createMeasurementData,
      loading: createMeasurementLoading,
      error: createMeasurementError,
    },
  ] = useCreateCargoMeasurementMutation({
    update: (cache, { data }) => {
      const mr = data?.createCargoMeasurement?.cargoMeasurement;
      if (!mr) return;

      cache.writeFragment({
        data: mr,
        fragment: gql`
          fragment NewMR on CargoMeasurement {
            id
            orderId
            locationId
            time
            measuredWeight
            rejectedWeight
            weightUnit
            measuredVolume
            rejectedVolume
            volumeUnit
          }
        `,
      });
    },
    refetchQueries: ['VoyagesTable_MeasurementReports'],
  });
  const [
    updateCargoMeasurement,
    {
      data: updateMeasurementData,
      loading: updateMeasurementLoading,
      error: updateMeasurementError,
    },
  ] = useUpdateCargoMeasurementMutation();
  const saveLoading = createMeasurementLoading || updateMeasurementLoading;
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);

  useEffect(
    () => setSaveSuccess(!!createMeasurementData || !!updateMeasurementData),
    [createMeasurementData, updateMeasurementData],
  );
  useEffect(
    () => setSaveError(!!createMeasurementError || !!updateMeasurementError),
    [createMeasurementError, updateMeasurementError],
  );
  useEffect(() => {
    setSaveSuccess(false);
    setSaveError(false);
  }, [portCallId]);

  const defaultValues = useMemo<MRFormFields>(() => {
    const existingMeasurements: MeasurementType[] = measurements.map((mr) => ({
      id: mr.id,
      orderId: mr.orderId,
      locationId: mr.locationId ?? null,
      measuredVolume: mr.measuredVolume ? +mr.measuredVolume : null,
      rejectedVolume: mr.rejectedVolume ? +mr.rejectedVolume : null,
      volumeUnit: mr.volumeUnit ?? null,
      measuredWeight: mr.measuredWeight ? +mr.measuredWeight : null,
      rejectedWeight: mr.rejectedWeight ? +mr.rejectedWeight : null,
      weightUnit: mr.weightUnit ?? null,
    }));

    const missingMeasurements: MeasurementType[] = locationId
      ? orders
          .filter(
            (order) =>
              existingMeasurements.findIndex((mr) => mr.orderId === order.id) <
              0,
          )
          .map((order) => ({
            id: null,
            orderId: order.id,
            locationId,
            measuredVolume: null,
            rejectedVolume: null,
            volumeUnit: null,
            measuredWeight: null,
            rejectedWeight: null,
            weightUnit: null,
          }))
      : [];

    return {
      measurements: [...existingMeasurements, ...missingMeasurements].sort(
        (a, b) => {
          if (a.orderId !== null && b.orderId !== null) {
            return a.orderId - b.orderId;
          }
          return -1;
        },
      ),
    };
  }, [locationId, measurements, orders]);

  const { control, handleSubmit, reset, register } = useForm<MRFormFields>({
    defaultValues,
    resolver: zodResolver(MRSchema),
  });

  useEffect(() => reset(defaultValues), [defaultValues, reset]);

  const { fields } = useFieldArray({
    control,
    name: 'measurements',
    keyName: 'key',
  });

  const onFormSubmit = async (data: MRFormFields) => {
    try {
      const newMeasurements = data.measurements;
      const oldMeasurements = measurements;

      const measurementsToUpdate = intersectionWith(
        newMeasurements,
        oldMeasurements,
        (a, b) =>
          a.id === b.id &&
          (a.orderId !== b.orderId ||
            a.locationId !== b.locationId ||
            a.weightUnit !== b.weightUnit ||
            a.volumeUnit !== b.volumeUnit ||
            a.measuredWeight !== b.measuredWeight ||
            a.measuredVolume !== b.measuredVolume ||
            a.rejectedWeight !== b.rejectedWeight ||
            a.rejectedVolume !== b.rejectedVolume),
      ).filter((bolDatum): bolDatum is Id<typeof bolDatum> => !!bolDatum.id);

      const measurementsToCreate = differenceWith(
        newMeasurements,
        oldMeasurements,
        (a, b) => a.id === b.id,
      );

      await Promise.all(
        measurementsToUpdate.map(({ id, ...measurement }) =>
          updateCargoMeasurement({
            variables: { input: { id, cargoMeasurementPatch: measurement } },
          }),
        ),
      );

      await Promise.all(
        measurementsToCreate.map(({ id, ...measurement }) =>
          createCargoMeasurement({
            variables: {
              input: {
                cargoMeasurement: {
                  ...measurement,
                  userId,
                  time: new Date().toISOString(), // Currently actual time of measurement is not used
                },
              },
            },
          }),
        ),
      );

      onSave?.();
    } catch (error) {
      console.error('Error validating measurement reports to save', error);
    }
  };

  const findBolData = (order: ScenarioOrder) => {
    const loadPortCallId = order.orderPortCallRelationsByOrderId.nodes.find(
      (pcr) => pcr?.isLoadPort,
    )?.portCallId;
    if (loadPortCallId == null) {
      return null;
    }
    return (
      portcalls
        .get(loadPortCallId)
        ?.billOfLadingsByPortCallId.nodes[0]?.billOfLadingDataByBillOfLadingId.nodes.filter(
          isDefined,
        )
        .find((b) => b.orderId === order.id) ?? null
    );
  };

  return (
    <Form border="none" onSubmit={handleSubmit(onFormSubmit)}>
      <FormSection title="Measurement report">
        <Box
          width="100%"
          borderWidth={1}
          borderStyle="solid"
          borderColor="neutral.gray_225"
          mb={3}
          overflow="scroll"
        >
          <Table style={{ tableLayout: 'auto' }}>
            <thead>
              <TableHeaders>
                <TableHeader minWidth={150}>Order ID</TableHeader>
                <TableHeader minWidth={150}>Receiving</TableHeader>
                <TableHeader minWidth={150}>Commodity</TableHeader>
                <TableHeader minWidth={150}>BoL weight</TableHeader>
                <TableHeader minWidth={200}>Measured weight</TableHeader>
                <TableHeader minWidth={200}>Rejected weight</TableHeader>
                <TableHeader minWidth={200}>Weight unit</TableHeader>
                <TableHeader minWidth={150}>BoL volume</TableHeader>
                <TableHeader minWidth={200}>Measured volume</TableHeader>
                <TableHeader minWidth={200}>Rejected volume</TableHeader>
                <TableHeader minWidth={200}>Volume unit</TableHeader>
                <TableHeader />
              </TableHeaders>
            </thead>
            <tbody>
              {fields.map((field, index) => {
                const order = orders.find(
                  (order) => order.id === field.orderId,
                );

                const bolWeight = order && findBolData(order)?.weight;
                const bolWeightUnit = order && findBolData(order)?.weightUnit;
                const bolVolume = order && findBolData(order)?.volume;
                const bolVolumeUnit = order && findBolData(order)?.volumeUnit;

                return (
                  <TableRow key={field.key}>
                    <TableCell>{order && getOrderDisplayName(order)}</TableCell>
                    <TableCell>
                      {organizations.get(
                        order?.orderDetailsByOrderId.nodes[0]
                          ?.receiverOrganisationId ?? NaN,
                      )?.name ?? 'No name'}
                    </TableCell>
                    <TableCell>
                      {order?.assortmentByAssortmentId?.name}
                    </TableCell>
                    <TableCell>
                      {bolWeight
                        ? `${bolWeight} ${
                            bolWeightUnit
                              ? massCapacityUnits[bolWeightUnit]?.symbol
                              : ''
                          }`
                        : undefined}
                    </TableCell>
                    <TableCell>
                      <input
                        type="hidden"
                        {...register(`measurements.${index}.id`, {
                          setValueAs: stringToNumber,
                        })}
                        defaultValue={field.id?.toString()}
                      />
                      <input
                        type="hidden"
                        {...register(`measurements.${index}.orderId`, {
                          setValueAs: stringToNumber,
                        })}
                        defaultValue={field.orderId?.toString()}
                      />
                      <input
                        type="hidden"
                        {...register(`measurements.${index}.locationId`, {
                          setValueAs: stringToNumber,
                        })}
                        defaultValue={field.locationId?.toString()}
                      />
                      <Input
                        type="number"
                        {...register(`measurements.${index}.measuredWeight`, {
                          setValueAs: stringToNumber,
                        })}
                        defaultValue={field.measuredWeight?.toString()}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        {...register(`measurements.${index}.rejectedWeight`, {
                          setValueAs: stringToNumber,
                        })}
                        defaultValue={field.rejectedWeight?.toString()}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        {...register(`measurements.${index}.weightUnit`)}
                        defaultValue={field.weightUnit?.toString()}
                        options={massUnitOptions}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell>
                      {bolVolume
                        ? `${bolVolume} ${
                            bolVolumeUnit
                              ? volumeCapacityUnits[bolVolumeUnit]?.symbol
                              : ''
                          }`
                        : undefined}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        {...register(`measurements.${index}.measuredVolume`, {
                          setValueAs: stringToNumber,
                        })}
                        defaultValue={field.measuredVolume?.toString()}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        {...register(`measurements.${index}.rejectedVolume`, {
                          setValueAs: stringToNumber,
                        })}
                        defaultValue={field.rejectedVolume?.toString()}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        {...register(`measurements.${index}.volumeUnit`)}
                        defaultValue={field.volumeUnit?.toString()}
                        options={volumeUnitOptions}
                        disabled={readOnly}
                      />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </Box>
        {!readOnly && (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Button
              disabled={saveLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={handleSubmit(onFormSubmit)}
              mr={3}
            >
              Save Measurement Report
            </Button>
            {saveSuccess && (
              <Text fontWeight="bold" color="#5fad09">
                ✓ Saved successfully
              </Text>
            )}
            {saveError && (
              <Text fontWeight="bold" color="#FC4A15">
                ✗ Error saving
              </Text>
            )}
          </Box>
        )}
      </FormSection>
    </Form>
  );
};

export default MRForm;
